import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, X, Loader2, ImagePlus, FileDown, Send, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 10;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * BreakdownProposalGenerator — pastes-to-PDF surface.
 *
 * The broker drops in screenshots / pastes deal notes and clicks
 * "Generate Proposal". The backend (`/api/breakdown/generate-proposal-pdf`)
 * returns a polished multi-page PDF which is shown inline via an <iframe>.
 *
 * The broker can then:
 *   - Download the PDF
 *   - Hit "Send Live Proposal" to share via the existing meeting flow
 *     (this calls onSendLive with the proposal data the broker reviewed)
 *   - Regenerate with more / different inputs
 */
const BreakdownProposalGenerator = ({ onSendLive }) => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [images, setImages] = useState([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // PDF state
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfMeta, setPdfMeta] = useState(null); // { clientName, legs, filename }

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = incoming.filter((f) => ACCEPTED.includes(f.type));
    const rejected = incoming.length - accepted.length;
    if (rejected > 0) {
      toast.error(`Skipped ${rejected} file(s) — only JPG / PNG / WEBP supported.`);
    }
    setImages((prev) => {
      const slotsLeft = MAX_IMAGES - prev.length;
      if (slotsLeft <= 0) {
        toast.error(`Already attached ${MAX_IMAGES} screenshots.`);
        return prev;
      }
      const next = accepted.slice(0, slotsLeft).map((f) => ({
        file: f,
        preview: URL.createObjectURL(f),
        id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      }));
      return [...prev, ...next];
    });
  };

  const removeImage = (id) => {
    setImages((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((i) => i.id !== id);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const pasted = [];
    for (const it of items) {
      if (it.kind === 'file' && it.type?.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) pasted.push(f);
      }
    }
    if (pasted.length) {
      e.preventDefault();
      addFiles(pasted);
    }
  };

  const reset = () => {
    images.forEach((i) => { if (i.preview) URL.revokeObjectURL(i.preview); });
    setImages([]);
    setNotes('');
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfMeta(null);
  };

  const generate = async () => {
    if (images.length === 0 && !notes.trim()) {
      toast.error('Add at least one screenshot or some deal notes first.');
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    images.forEach((i) => fd.append('images', i.file, i.file.name));
    if (notes.trim()) fd.append('notes', notes.trim());

    try {
      const res = await fetch(`${BACKEND}/api/breakdown/generate-proposal-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          detail = j.detail || JSON.stringify(j);
        } catch (e) { /* ignore */ }
        throw new Error(detail);
      }
      const clientName = res.headers.get('X-Proposal-Client') || 'Client';
      const legs = parseInt(res.headers.get('X-Proposal-Legs') || '0', 10);
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `DerwentProposal_${clientName.replace(/\s+/g, '_')}.pdf`;

      const blob = await res.blob();
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setPdfMeta({ clientName, legs, filename });
      toast.success(`Proposal generated for ${clientName} (${legs} leg${legs === 1 ? '' : 's'}).`);
    } catch (e) {
      toast.error(`Generation failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = () => {
    if (!pdfBlobUrl || !pdfMeta) return;
    const a = document.createElement('a');
    a.href = pdfBlobUrl;
    a.download = pdfMeta.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Card className="border-2 border-purple-200 shadow-lg" data-testid="proposal-generator-card">
      <CardHeader className="bg-gradient-to-r from-purple-50 via-fuchsia-50 to-indigo-50">
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          Generate Proposal from Screenshots &amp; Notes
        </CardTitle>
        <p className="text-xs text-slate-600 mt-1">
          Drop screenshots and/or paste deal notes — Claude Sonnet 4.5 produces a polished multi-page PDF proposal you can send straight to clients.
        </p>
      </CardHeader>

      <CardContent className="pt-6 space-y-4">
        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onPaste={onPaste}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 transition-colors cursor-pointer text-center ${
            dragOver ? 'border-purple-500 bg-purple-50' : 'border-slate-300 hover:border-purple-400 bg-slate-50'
          }`}
          data-testid="proposal-dropzone"
          tabIndex={0}
        >
          <ImagePlus className="h-8 w-8 mx-auto mb-2 text-slate-400" />
          <p className="text-sm font-medium text-slate-700">
            Drag screenshots here, paste from clipboard, or <span className="text-purple-600 underline">browse</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">JPG, PNG, or WEBP · up to {MAX_IMAGES} files</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            data-testid="proposal-file-input"
          />
        </div>

        {images.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="proposal-previews">
            {images.map((i) => (
              <div key={i.id} className="relative group rounded-lg overflow-hidden border border-slate-200">
                <img src={i.preview} alt={i.file.name} className="w-full h-20 object-cover" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeImage(i.id); }}
                  className="absolute top-1 right-1 bg-slate-900/75 hover:bg-rose-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                  aria-label="Remove image"
                  data-testid={`proposal-remove-${i.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Deal Notes <span className="text-slate-400 font-normal">(optional — paste your shorthand here)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={`Example:
Client name is Kris & Emma.

__Refinance Investment Property__
$1,215,000 Security Value
$335,211 Debt
$176 Mortgage Registration
$1500 Lender Fee
$972,000 Loan Amount
80% LVR

__Purchase New Owner Occupied Property__
$1,540,000 Property Value
$66,712 Stamp Duty
$976,626 Proposed Debt
63.42% LVR`}
            rows={6}
            maxLength={12000}
            className="w-full rounded-lg border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
            data-testid="proposal-notes"
          />
          <p className="text-xs text-slate-400 mt-1">{notes.length} / 12000 characters</p>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            onClick={generate}
            disabled={submitting || (images.length === 0 && !notes.trim())}
            className="flex-1 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white"
            data-testid="proposal-generate-btn"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating PDF…</>
            ) : pdfBlobUrl ? (
              <><RefreshCcw className="h-4 w-4 mr-2" /> Re-generate</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate Proposal</>
            )}
          </Button>
          {(images.length > 0 || notes.trim() || pdfBlobUrl) && !submitting && (
            <Button
              variant="outline"
              onClick={reset}
              className="border-slate-300"
              data-testid="proposal-clear-btn"
            >
              Clear
            </Button>
          )}
        </div>

        <p className="text-[11px] text-slate-400 leading-snug">
          Screenshots are sent to Anthropic for extraction and immediately discarded — they are never stored.
        </p>

        {/* Inline PDF preview */}
        {pdfBlobUrl && (
          <div className="mt-4 border-2 border-slate-200 rounded-xl overflow-hidden bg-slate-100" data-testid="proposal-pdf-preview-wrap">
            <div className="flex items-center justify-between bg-slate-900 text-white px-4 py-2">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Preview</p>
                <p className="text-sm font-semibold">{pdfMeta?.filename}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadPdf}
                  className="bg-white hover:bg-slate-100 text-slate-900 border-0"
                  data-testid="proposal-download-btn"
                >
                  <FileDown className="h-4 w-4 mr-1" /> Download
                </Button>
                {onSendLive && (
                  <Button
                    size="sm"
                    onClick={() => onSendLive(pdfMeta, pdfBlobUrl)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="proposal-send-live-btn"
                  >
                    <Send className="h-4 w-4 mr-1" /> Send Live Proposal
                  </Button>
                )}
              </div>
            </div>
            {/* iframe is the most reliable way to show a PDF inline across PC + mobile. */}
            <iframe
              src={pdfBlobUrl}
              title="Proposal preview"
              className="w-full"
              style={{ height: '78vh', border: 'none' }}
              data-testid="proposal-pdf-iframe"
            />
            <p className="text-[11px] text-slate-500 px-4 py-2 bg-slate-50">
              PDF is locally rendered in your browser. On mobile, tap Download to open in your default viewer.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BreakdownProposalGenerator;
